export interface ParsedPart {
  headers: Record<string, string>
  body: Uint8Array
}

export function findBoundary(buf: Uint8Array, boundary: string) {
  const encoder = new TextEncoder()
  const cand1 = encoder.encode('\r\n' + boundary)
  const cand2 = encoder.encode('\n' + boundary)
  const cand3 = encoder.encode(boundary)

  const search = (pattern: Uint8Array) => {
    for (let i = 0; i <= buf.length - pattern.length; i++) {
      let ok = true
      for (let j = 0; j < pattern.length; j++) {
        if (buf[i + j] !== pattern[j]) {
          ok = false
          break
        }
      }
      if (ok) return i
    }
    return -1
  }

  const i1 = search(cand1)
  const i2 = search(cand2)
  const i3 = search(cand3)

  let earliest = -1
  if (i1 !== -1) earliest = i1
  if (i2 !== -1 && (earliest === -1 || i2 < earliest)) earliest = i2
  if (i3 !== -1 && (earliest === -1 || i3 < earliest)) earliest = i3

  let matchedPatternLength = 0
  if (earliest !== -1) {
    if (earliest === i1) matchedPatternLength = cand1.length
    else if (earliest === i2) matchedPatternLength = cand2.length
    else matchedPatternLength = cand3.length
  }

  return {index: earliest, length: matchedPatternLength}
}

export function indexOfPattern(buf: Uint8Array, pattern: Uint8Array, from = 0) {
  for (let i = from; i <= buf.length - pattern.length; i++) {
    let match = true
    for (let j = 0; j < pattern.length; j++) {
      if (buf[i + j] !== pattern[j]) {
        match = false
        break
      }
    }
    if (match) return i
  }
  return -1
}

export class StreamingMultipartParser {
  private buffer = new Uint8Array()
  private isFirst = true
  private boundary: string
  private doubleCRLF = new TextEncoder().encode('\r\n\r\n')

  constructor(boundary: string) {
    this.boundary = `--${boundary}`
  }

  append(chunk: Uint8Array) {
    const tmp = new Uint8Array(this.buffer.length + chunk.length)
    tmp.set(this.buffer, 0)
    tmp.set(chunk, this.buffer.length)
    this.buffer = tmp
  }

  *parse(isDone: boolean): Generator<ParsedPart> {
    while (true) {
      const {index: boundaryIdx, length: patternLen} = findBoundary(this.buffer, this.boundary)
      if (boundaryIdx === -1) break

      const partStart = boundaryIdx + patternLen
      let content_start = partStart
      if (this.buffer[content_start] === 13 && this.buffer[content_start + 1] === 10)
        content_start += 2

      const headerEndIdx = indexOfPattern(this.buffer, this.doubleCRLF, content_start)
      if (headerEndIdx === -1) {
        if (isDone) this.buffer = new Uint8Array()
        break
      }

      const headerBytes = this.buffer.slice(content_start, headerEndIdx)
      const headerText = new TextDecoder().decode(headerBytes)
      const headers: Record<string, string> = {}
      for (const line of headerText.split('\r\n')) {
        const idx = line.indexOf(':')
        if (idx !== -1)
          headers[line.slice(0, idx).trim().toLowerCase()] = line.slice(idx + 1).trim()
      }

      const contentLen = parseInt(headers['content-length'] || headers['contentlength'] || '0', 10)
      const bodyStart = headerEndIdx + this.doubleCRLF.length

      if (contentLen > 0) {
        if (this.buffer.length < bodyStart + contentLen && !isDone) break

        const availableLen = Math.min(contentLen, Math.max(0, this.buffer.length - bodyStart))
        const body = this.buffer.slice(bodyStart, bodyStart + availableLen)

        yield {headers, body}

        let consumeEnd = bodyStart + availableLen
        if (this.buffer[consumeEnd] === 13 && this.buffer[consumeEnd + 1] === 10) consumeEnd += 2
        this.buffer = this.buffer.subarray(consumeEnd)
        this.isFirst = false
        continue
      }

      // If content-length is not provided, we need the next boundary to know where the body ends
      const nextBoundary = findBoundary(this.buffer.subarray(bodyStart), this.boundary)
      if (nextBoundary.index === -1 && !isDone) break

      const bodyEnd =
        nextBoundary.index === -1 ? this.buffer.length : bodyStart + nextBoundary.index
      const body = this.buffer.slice(bodyStart, bodyEnd)

      yield {headers, body}

      let consumeEnd = bodyEnd
      if (this.buffer[consumeEnd] === 13 && this.buffer[consumeEnd + 1] === 10) consumeEnd += 2
      this.buffer = this.buffer.subarray(consumeEnd)
      this.isFirst = false
    }
  }

  getLeftover() {
    return this.buffer
  }
}
